
import { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { initSecurityMonitoring, setupCSP } from "@/utils/security";
import { supabase } from "@/integrations/supabase/client";

interface SecurityContextType {
  isSecurityInitialized: boolean;
  securityLevel: 'standard' | 'enhanced';
  setSecurityLevel: (level: 'standard' | 'enhanced') => void;
  reportSecurityEvent: (eventType: string, details: any) => void;
}

const SecurityContext = createContext<SecurityContextType>({
  isSecurityInitialized: false,
  securityLevel: 'standard',
  setSecurityLevel: () => {},
  reportSecurityEvent: () => {},
});

export const useSecurityContext = () => useContext(SecurityContext);

export const SecurityProvider = ({ children }: { children: ReactNode }) => {
  const [isSecurityInitialized, setIsSecurityInitialized] = useState(false);
  const [securityLevel, setSecurityLevel] = useState<'standard' | 'enhanced'>('standard');
  const [securityEvents, setSecurityEvents] = useState<Array<{type: string, details: any, timestamp: Date}>>([]);
  
  useEffect(() => {
    // Initialize security features
    try {
      // Set up CSP headers
      setupCSP();
      
      // Initialize security monitoring
      initSecurityMonitoring();
      
      // Set devtools detection
      if (process.env.NODE_ENV === 'production') {
        const devToolsDetection = () => {
          const widthThreshold = window.outerWidth - window.innerWidth > 160;
          const heightThreshold = window.outerHeight - window.innerHeight > 160;
          
          if (widthThreshold || heightThreshold) {
            console.warn('[Security] DevTools may be open');
            reportSecurityEvent('devtools-detected', { widthThreshold, heightThreshold });
          }
        };
        
        window.addEventListener('resize', devToolsDetection);
        setInterval(devToolsDetection, 3000);
      }
      
      // Mark security as initialized
      setIsSecurityInitialized(true);
      console.log('[Security] Provider initialized successfully');
    } catch (error) {
      console.error('[Security] Failed to initialize security features:', error);
    }
  }, []);
  
  // Effect for applying security level changes
  useEffect(() => {
    if (securityLevel === 'enhanced') {
      console.log('[Security] Enhanced security mode activated');
      // Apply additional security measures for enhanced mode
      
      // In a real implementation, this would apply stricter policies
    }
  }, [securityLevel]);
  
  const reportSecurityEvent = async (eventType: string, details: any) => {
    const newEvent = {
      type: eventType,
      details,
      timestamp: new Date()
    };
    
    setSecurityEvents(prev => [...prev, newEvent]);
    
    // Log security events
    console.warn(`[Security Event] ${eventType}`, details);
    
    // Determine severity based on event type
    const severity = ['authentication-failure', 'suspicious-activity', 'sql-injection-attempt'].includes(eventType)
      ? 'high'
      : ['rate-limit-exceeded', 'invalid-input'].includes(eventType)
      ? 'medium'
      : 'low';
    
    // For critical events, notify the user
    if (severity === 'high') {
      toast.error("Security alert: Suspicious activity detected");
    }
    
    // Send to security monitoring edge function
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await supabase.functions.invoke('security-monitoring', {
        body: {
          event: {
            event_type: eventType,
            severity,
            message: `Security event: ${eventType}`,
            user_id: user?.id,
            details,
            timestamp: new Date().toISOString()
          }
        }
      });
    } catch (error) {
      console.error('[Security] Failed to log security event:', error);
    }
  };
  
  return (
    <SecurityContext.Provider 
      value={{ 
        isSecurityInitialized, 
        securityLevel, 
        setSecurityLevel,
        reportSecurityEvent
      }}
    >
      {children}
    </SecurityContext.Provider>
  );
};

export default SecurityProvider;
